import { SEO_LENGTHS } from '@ecom/contracts';
import type { CheckLevel, Language, SeoCheck } from '@/lib/content-pages';

/**
 * A product's, category's, brand's or post's search result, checked as the
 * storefront will write it (ADR-095): the title it shows, the description —
 * its own, or what will stand in for one — both languages, a share picture,
 * and whether it is hidden at all. Worst first.
 */
export function entitySeoChecks(input: {
  language: Language;
  /** The title a result will show in this language — the store's pattern applied where there is one. */
  title: string;
  /** Written for search in this language. */
  description: string;
  /** What stands in when nothing was written: a pattern, the item's own words. */
  fallbackDescription: string;
  /** Whether the other language has a name at all. */
  otherWritten: boolean;
  hasImage: boolean;
  noindex: boolean;
}): SeoCheck[] {
  const checks: SeoCheck[] = [];
  const add = (id: string, level: CheckLevel, message: SeoCheck['message'], values?: SeoCheck['values']) => checks.push({ id, level, message, values });

  const title = input.title.trim();
  const { min: tMin, max: tMax } = SEO_LENGTHS.title;
  if (!title) add('title', 'bad', 'seoTitleMissing');
  else if (title.length > tMax) add('title', 'warn', 'seoTitleLong', { count: title.length, max: tMax });
  else if (title.length < tMin) add('title', 'warn', 'seoTitleShort', { count: title.length, min: tMin });
  else add('title', 'good', 'seoTitleGood', { count: title.length });

  const description = input.description.trim();
  const { min: dMin, max: dMax } = SEO_LENGTHS.description;
  if (!description) add('description', input.fallbackDescription.trim() ? 'warn' : 'bad', 'seoDescriptionAuto');
  else if (description.length > dMax) add('description', 'warn', 'seoDescriptionLong', { count: description.length, max: dMax });
  else if (description.length < dMin) add('description', 'warn', 'seoDescriptionShort', { count: description.length, min: dMin });
  else add('description', 'good', 'seoDescriptionGood', { count: description.length });

  if (!input.otherWritten) add('other', 'bad', input.language === 'ar' ? 'seoEnglishMissing' : 'seoArabicMissing');
  if (input.noindex) add('index', 'warn', 'seoHidden');
  add('image', input.hasImage ? 'good' : 'warn', input.hasImage ? 'seoImageGood' : 'seoImageMissing');

  const order: Record<CheckLevel, number> = { bad: 0, warn: 1, good: 2 };
  return checks.sort((a, b) => order[a.level] - order[b.level]);
}
